import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import QuizComponent from '../../components/QuizComponent';
import { Users, BookOpen, MessageSquare, Clock, CheckCircle, AlertTriangle, Heart } from 'lucide-react';

const leadershipContent = {
  principles: [
    {
      id: 'never-mix',
      title: 'Never Mix Law and Grace',
      content: `This is the most important principle for grace-centered leadership: NEVER mix law and grace. They are mutually exclusive systems.

"For sin will have no dominion over you, since you are not under law but under grace." (Romans 6:14 ESV)

"But if you are led by the Spirit, you are not under the law." (Galatians 5:18 ESV)

When you mix law and grace, you:
• Confuse people about their standing with God
• Put them back under what Christ freed them from
• Undermine the power of grace to transform
• Produce either pride (in law-keepers) or despair (in law-breakers)

The Ten Commandments are not the Christian's rule of life. We are led by the Spirit, not by written code (Romans 7:6). This doesn't mean we live lawlessly—it means we live by a higher power.

**Practical application:**
• Don't use law to motivate behavior
• Don't imply the OT sacrificial/ceremonial system applies today
• Don't suggest believers need to "get right" with God
• Always anchor teaching in the finished work`
    },
    {
      id: 'never-motivate-fear',
      title: 'Never Motivate with Fear or Guilt',
      content: `Fear and guilt are not tools of the Holy Spirit for believers. They are tools of the enemy.

"For you did not receive the spirit of slavery to fall back into fear, but you have received the Spirit of adoption as sons." (Romans 8:15 ESV)

"There is no fear in love, but perfect love casts out fear. For fear has to do with punishment, and whoever fears has not been perfected in love." (1 John 4:18 ESV)

If your teaching produces fear, something is wrong with the teaching—not with the listeners. The Spirit produces assurance, confidence, and peace—not anxiety.

**Replace fear with truth:**
• Instead of "God is disappointed in you" → "You are fully accepted in Christ"
• Instead of "You might lose your salvation" → "You are sealed forever"
• Instead of "Sin breaks fellowship" → "Nothing separates you from God's love"
• Instead of "God will judge your sins" → "Your sins are already judged at the cross"

Love motivates. Truth liberates. Fear enslaves.`
    },
    {
      id: 'always-affirm',
      title: 'Always Affirm: Forgiveness, Identity, Union, Security',
      content: `Every teaching, every lesson, every conversation should reinforce these four truths:

**1. TOTAL FORGIVENESS**
All sins—past, present, future—forgiven at the cross. Not progressively. Not conditionally. Completely. (Colossians 2:13-14; Hebrews 10:14)

**2. NEW IDENTITY**
Believers have a new heart, are new creations, with sin as a parasite—not their true self. (Ezekiel 36:26; 2 Corinthians 5:17; Romans 7:17)

**3. PERMANENT UNION**
One spirit with Christ. Never distant. Never "out of fellowship." Always close. (1 Corinthians 6:17)

**4. ETERNAL SECURITY**
Sealed by the Spirit. Perfected forever. Nothing can separate from God's love. (Ephesians 1:13-14; Hebrews 10:14; Romans 8:38-39)

**The test:** After your teaching, do people feel MORE secure in Christ or LESS secure? More loved or more condemned? More restful or more anxious? Grace-centered teaching produces security, love, and rest.`
    },
    {
      id: 'teach-resting',
      title: 'Teach Resting, Not Striving',
      content: `Replace all "try harder/surrender/get closer/do more" language with trust and rest.

"Come to me, all who labor and are heavy laden, and I will give you rest. Take my yoke upon you, and learn from me, for I am gentle and lowly in heart, and you will find rest for your souls. For my yoke is easy, and my burden is light." (Matthew 11:28-30 ESV)

**Language to AVOID:**
• "Surrender more"
• "Try harder"
• "Get closer to God"
• "Do more for God"
• "Maintain your fellowship"
• "Keep short accounts"

**Language to USE:**
• "Trust Christ in you"
• "Rest in the finished work"
• "Remember who you are"
• "Offer yourself to Him"
• "Let grace train you"
• "Thank God you're forgiven"

The Christian life is not about striving to be what you already are. It's about believing what's true and letting that transform your living.`
    },
    {
      id: 'handle-sin',
      title: 'How to Handle Sin in Discipleship',
      content: `When someone shares sin struggles, the grace response is different from the religious response:

**RELIGIOUS RESPONSE:**
"You need to confess, repent, and get right with God. Pray for forgiveness. Try harder next time. God is disappointed but will forgive you if you're sincere."

**GRACE RESPONSE:**
"That wasn't the real you—the new creation in Christ. You're already forgiven—thank God for that. Sin doesn't define you or separate you from God. Now, let's look at what lies you might be believing that led to this. Trust Christ's life in you."

The goal isn't behavior modification through guilt—it's renewing the mind through truth. When people know who they really are, behavior follows.

**Key truths to reinforce:**
• Sin doesn't change your identity or standing
• You don't need to ask for forgiveness you already have
• Sin is a parasite, not the real you (Romans 7:17)
• Grace trains—not condemnation (Titus 2:11-12)
• The Spirit convicts by reminding you of who you are, not by condemning`
    }
  ],
  studyGuides: [
    {
      title: 'New Covenant Foundations',
      weeks: 4,
      description: 'A 4-week journey through core grace truths for any small group.',
      sessions: [
        { week: 1, topic: 'Total Forgiveness', focus: 'Ephesians 1:7; Colossians 2:13-14; Hebrews 10:14', application: 'Switch from "forgive me" to "thank You I\'m forgiven"' },
        { week: 2, topic: 'New Heart & New Creation', focus: 'Ezekiel 36:26-27; 2 Corinthians 5:17; Romans 7:17,20', application: 'Identify the "real you" vs. sin as parasite' },
        { week: 3, topic: 'United & Sealed', focus: '1 Corinthians 6:17; Ephesians 1:13-14; Romans 8:38-39', application: 'Rest in permanent closeness to God' },
        { week: 4, topic: 'Led by the Spirit', focus: 'Romans 7:6; Galatians 5:18; Titus 2:11-12', application: 'Trust grace to train, not law to constrain' },
      ]
    }
  ],
  discussionFormat: {
    title: 'Grace-Centered Discussion Format',
    steps: [
      { name: 'Identity Reminder', time: '5 min', description: 'Start by affirming: "You are forgiven, united, complete in Christ."' },
      { name: 'Read Together', time: '5 min', description: 'Read the passage aloud as a group.' },
      { name: 'What Does It Say?', time: '10 min', description: 'Observations—what does the text actually say?' },
      { name: 'New Covenant Filter', time: '10 min', description: 'Is this pre/post cross? Old/New Covenant? Who is addressed?' },
      { name: 'How Does This Align?', time: '10 min', description: 'Does our reading align with total forgiveness, union, security?' },
      { name: 'Identity Application', time: '10 min', description: 'How does this truth shape how we see ourselves and live?' },
      { name: 'Thank, Don\'t Ask', time: '5 min', description: 'Close by thanking God for who we are, not asking for what we have.' },
    ]
  }
};

export default function LeadersHub() {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="leaders-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1637109032180-ffda4ae86fbe?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Coffee and bible representing discipleship and leadership"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 7 · Multiplication
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              For Leaders
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              Grace-centered leadership and teaching tools
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="principles" className="space-y-8">
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="principles" data-testid="tab-principles">Principles</TabsTrigger>
            <TabsTrigger value="guides" data-testid="tab-guides">Study Guides</TabsTrigger>
            <TabsTrigger value="format" data-testid="tab-format">Format</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          <TabsContent value="principles" className="space-y-8">
            {/* Warning */}
            <Card className="max-w-3xl bg-destructive/5 border-destructive/20">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  Leader Commitment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 mb-4">
                  As a leader, you shape how people understand God. Mixing law and grace, motivating with 
                  fear, or teaching conditional forgiveness can deeply harm those you lead. Commit to:
                </p>
                <ul className="space-y-2">
                  {[
                    'Never mixing law and grace',
                    'Never motivating with fear or guilt',
                    'Always affirming total forgiveness, new identity, permanent union, eternal security',
                    'Teaching resting in Christ, not striving for acceptance',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Principles Accordion */}
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {leadershipContent.principles.map((principle, index) => (
                  <AccordionItem 
                    key={principle.id} 
                    value={principle.id}
                    className="border rounded-lg px-6"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                          {index + 1}
                        </span>
                        <span className="font-serif text-lg font-semibold">{principle.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <div className="pl-11 prose prose-slate max-w-none">
                        {principle.content.split('\n\n').map((paragraph, pIndex) => (
                          <p key={pIndex} className="text-foreground/80 leading-relaxed mb-4 last:mb-0 whitespace-pre-wrap">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </TabsContent>

          <TabsContent value="guides" className="space-y-8">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                This study guide covers the core New Covenant truths in 4 weeks. Each session builds 
                on the previous and includes specific application for transformation.
              </p>
            </div>

            {leadershipContent.studyGuides.map((guide) => (
              <Card key={guide.title} className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    <span className="text-sm bg-primary/10 text-primary px-2 py-1 rounded-full">
                      {guide.weeks} weeks
                    </span>
                  </div>
                  <CardTitle className="font-serif">{guide.title}</CardTitle>
                  <CardDescription>{guide.description}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {guide.sessions.map((session) => (
                      <div key={session.week} className="p-4">
                        <div className="flex items-start gap-3">
                          <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">
                            {session.week}
                          </span>
                          <div className="flex-1">
                            <p className="font-semibold">{session.topic}</p>
                            <p className="text-sm text-primary mt-1">{session.focus}</p>
                            <p className="text-sm text-muted-foreground mt-2 italic">
                              Application: {session.application}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="format" className="space-y-8">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                This discussion format keeps grace central and avoids slipping into law-based teaching.
              </p>
            </div>

            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  {leadershipContent.discussionFormat.title}
                </CardTitle>
                <CardDescription>Approximately 55 minutes total</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {leadershipContent.discussionFormat.steps.map((step, index) => (
                  <div 
                    key={step.name}
                    className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold">{index + 1}</span>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold">{step.name}</h4>
                        <span className="text-xs bg-muted px-2 py-1 rounded-full flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {step.time}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Key Reminder */}
            <Card className="max-w-2xl mx-auto bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Remember
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 italic">
                  "After your teaching, people should feel MORE secure in Christ, not less. 
                  More loved, not more condemned. More restful, not more anxious. 
                  If they don't, something in the teaching needs to change."
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="leaders" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
